import {Link} from 'react-router-dom';

function normalizeImageUrl(value) {
    if (!value) {
        return '/img/fallback-min.png';
    }

    if (/^(https?:)?\/\//.test(value)) {
        return value;
    }

    return value.startsWith('/') ? value : `/${value}`;
}

function normalizeWebsiteUrl(value) {
    if (!value) {
        return '';
    }

    return /^(https?:)?\/\//i.test(value) ? value : `https://${value}`;
}

function getWebsiteLabel(value) {
    return String(value || '')
        .replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')
        .split('/')[0];
}

function getOrganizationUrl(item) {
    if (item?.url) {
        return item.url;
    }

    return `/organization/${item?.urlSlug || ''}`;
}

function getPrimaryValue(primaryValue, values) {
    if (primaryValue) {
        return primaryValue;
    }

    return Array.isArray(values) && values.length ? values[0] : '';
}

function getTypeModifier(label) {
    const normalizedLabel = String(label || '').toLowerCase();

    if (normalizedLabel.includes('фонд') || normalizedLabel.includes('fund')) {
        return 'fund';
    }

    if (normalizedLabel.includes('асоц') || normalizedLabel.includes('assoc')) {
        return 'assoc';
    }

    if (normalizedLabel.includes('лікар') || normalizedLabel.includes('hospital')) {
        return 'hospital';
    }

    if (normalizedLabel.includes('освіт') || normalizedLabel.includes('education')) {
        return 'education';
    }

    return 'default';
}

export default function OrganizationListItem({item, t}) {
    const translate = typeof t === 'function'
        ? t
        : ((key, fallback) => fallback || key);
    const organizationType = item?.type || item?.organizationType || {};
    const phones = Array.isArray(item?.phones) ? item.phones : [];
    const emails = Array.isArray(item?.emails) ? item.emails : [];
    const websites = Array.isArray(item?.websites) ? item.websites : [];
    const addresses = Array.isArray(item?.addresses) ? item.addresses : [];
    const organizationUrl = getOrganizationUrl(item);
    const primaryPhone = getPrimaryValue(item?.primaryPhone, phones);
    const primaryEmail = getPrimaryValue(item?.primaryEmail, emails);
    const primaryWebsite = getPrimaryValue(item?.primaryWebsite, websites);
    const primaryAddress = getPrimaryValue(item?.primaryAddress, addresses);
    const copyPayload = [
        primaryPhone && `Phone: ${primaryPhone}`,
        primaryEmail && `Email: ${primaryEmail}`,
        primaryWebsite && `Website: ${primaryWebsite}`,
        primaryAddress && `Address: ${primaryAddress}`,
    ].filter(Boolean).join('\n');
    const typeModifier = getTypeModifier(organizationType?.label);

    const handleCopyContacts = () => {
        if (!copyPayload || !window?.navigator?.clipboard?.writeText) {
            return;
        }

        window.navigator.clipboard.writeText(copyPayload).catch(() => {
        });
    };

    return (
        <article className="org-card">
            <div className="org-card__logo">
                <img src={normalizeImageUrl(item?.imageUrl || item?.image)} alt="" />
            </div>

            <div className="org-card__body">
                <div className="org-card__top">
                    <h2 className="org-card__name">{item?.name || ''}</h2>
                    {!!organizationType?.label && (
                        <span className={`org-card__type org-card__type--${typeModifier}`}>{organizationType.label}</span>
                    )}
                </div>

                <div className="org-card__contacts">
                    {!!primaryPhone && (
                        <a className="org-card__contact" href={`tel:${primaryPhone}`}>
                            <span className="org-card__contact-icon" aria-hidden="true">☎</span>
                            <span>{primaryPhone}</span>
                        </a>
                    )}

                    {!!primaryEmail && (
                        <a className="org-card__contact" href={`mailto:${primaryEmail}`}>
                            <span className="org-card__contact-icon" aria-hidden="true">✉</span>
                            <span>{primaryEmail}</span>
                        </a>
                    )}

                    {!!primaryWebsite && (
                        <a
                            className="org-card__contact"
                            href={normalizeWebsiteUrl(primaryWebsite)}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <span className="org-card__contact-icon" aria-hidden="true">🌐</span>
                            <span>{getWebsiteLabel(primaryWebsite)}</span>
                        </a>
                    )}
                </div>

                {!!primaryAddress && (
                    <div className="org-card__address">
                        <span className="org-card__address-icon" aria-hidden="true">📍</span>
                        <span>{primaryAddress}</span>
                    </div>
                )}

                <div className="org-card__actions">
                    <Link className="org-btn org-btn--primary" to={organizationUrl}>
                        {translate('organizations.browser.open', 'Open')}
                    </Link>
                    <button
                        type="button"
                        className="org-btn org-btn--ghost"
                        onClick={handleCopyContacts}
                        disabled={!copyPayload}
                    >
                        {translate('organizations.browser.copy', 'Copy contacts')}
                    </button>
                </div>
            </div>
        </article>
    );
}
